export type SfxName = 'click' | 'flip' | 'land' | 'match' | 'place' | 'capture' | 'move' | 'pay' | 'turn' | 'timer_tick' | 'win' | 'lose' | 'emote';

let context: AudioContext | null = null;
let muted = false;
let volume = 0.7;

export function configureSound(next: { muted: boolean; volume: number }) {
  muted = next.muted;
  volume = Math.max(0, Math.min(1, next.volume));
}

function ctx(): AudioContext | null {
  if (muted || typeof AudioContext === 'undefined') return null;
  context ??= new AudioContext();
  if (context.state === 'suspended') void context.resume();
  return context;
}

function tone(audio: AudioContext, frequency: number, duration: number, type: OscillatorType = 'sine', delay = 0, endFrequency?: number) {
  const start = audio.currentTime + delay;
  const oscillator = audio.createOscillator();
  const gain = audio.createGain();
  oscillator.type = type;
  oscillator.frequency.setValueAtTime(frequency, start);
  if (endFrequency) oscillator.frequency.exponentialRampToValueAtTime(endFrequency, start + duration);
  gain.gain.setValueAtTime(0.0001, start);
  gain.gain.exponentialRampToValueAtTime(Math.max(0.0001, volume * 0.12), start + 0.008);
  gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
  oscillator.connect(gain).connect(audio.destination);
  oscillator.start(start);
  oscillator.stop(start + duration + 0.02);
}

function noise(audio: AudioContext, duration: number, filterFrequency = 4_000) {
  const frames = Math.max(1, Math.floor(audio.sampleRate * duration));
  const buffer = audio.createBuffer(1, frames, audio.sampleRate);
  const data = buffer.getChannelData(0);
  crypto.getRandomValues(new Uint32Array(data.buffer));
  for (let index = 0; index < data.length; index += 1) data[index] = ((data[index] ?? 0) / 0xffff_ffff) * 2 - 1;
  const source = audio.createBufferSource();
  const filter = audio.createBiquadFilter();
  const gain = audio.createGain();
  filter.type = 'lowpass';
  filter.frequency.setValueAtTime(filterFrequency, audio.currentTime);
  filter.frequency.exponentialRampToValueAtTime(400, audio.currentTime + duration);
  gain.gain.setValueAtTime(volume * 0.05, audio.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.0001, audio.currentTime + duration);
  source.buffer = buffer;
  source.connect(filter).connect(gain).connect(audio.destination);
  source.start();
}

export function play(name: SfxName) {
  const audio = ctx();
  if (!audio) return;
  switch (name) {
    case 'click': noise(audio, 0.004); tone(audio, 880, 0.06); break;
    case 'flip': tone(audio, 300, 0.5, 'sine', 0, 900); tone(audio, 420, 0.45, 'square'); break;
    case 'land': tone(audio, 220, 0.08, 'triangle'); break;
    case 'match': tone(audio, 660, 0.12); tone(audio, 990, 0.15, 'sine', 0.09); break;
    case 'place': tone(audio, 520, 0.09, 'sine', 0, 420); break;
    case 'capture': tone(audio, 1_200, 0.2, 'square', 0, 700); break;
    case 'move': noise(audio, 0.25); break;
    case 'pay': tone(audio, 1_200, 0.16, 'square'); tone(audio, 1_600, 0.16, 'square', 0.09); break;
    case 'turn': tone(audio, 523, 0.3, 'triangle'); tone(audio, 659, 0.3, 'triangle'); break;
    case 'timer_tick': noise(audio, 0.002); tone(audio, 1_200, 0.04); break;
    case 'win': [523, 659, 784, 1047].forEach((frequency, index) => tone(audio, frequency, 0.22, 'triangle', index * 0.13)); break;
    case 'lose': tone(audio, 196, 0.3); tone(audio, 131, 0.4, 'sine', 0.2); break;
    case 'emote': tone(audio, 700, 0.08, 'sine', 0, 1_000); break;
  }
}
